import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { checkAuthStatus as checkAuth, logout as userLogout } from './services/authService';
import { updateUser } from './services/userService';
import { User } from './types/user';
import { Tenant } from './types/tenant'; // Import Tenant
import i18n from './i18n'; // Import i18n
import { registerNotificationToken, unregisterNotificationToken } from './services/notificationService';

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  currentTenant: Tenant | null; // Add currentTenant
  login: (userData: User) => void;
  logout: () => void;
  switchTenant: (tenant: Tenant) => void; // Add switchTenant
  updateCurrentTenant: (tenantData: Partial<Tenant>) => void;
  loading: boolean;
  updateUserPreferences: (theme: string, language: string) => Promise<void>;
  notificationError: string | null; // New: Global error for notifications
  setNotificationError: (message: string | null) => void; // New: Setter for notification error
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null); // Add currentTenant state
  const [loading, setLoading] = useState<boolean>(true);
  const [pushToken, setPushToken] = useState<{token: string, deviceType: string} | null>(null);
  const [notificationError, setNotificationError] = useState<string | null>(null); // New state for notification errors

  const checkAuthStatus = async () => {
    try {
      const data = await checkAuth();
      if (data.isAuthenticated) {
        setIsAuthenticated(true);
        setUser(data.user);
        if (data.user && data.user.tenants && data.user.tenants.length > 0) {
          const { currentTenantId, tenants } = data.user;
          const defaultTenant = currentTenantId
            ? tenants.find(t => t.id === currentTenantId)
            : tenants[0];
          setCurrentTenant(defaultTenant || tenants[0]);
        }
        if (data.user && data.user.language) {
          i18n.changeLanguage(data.user.language);
        }
      } else {
        setIsAuthenticated(false);
        setUser(null);
        setCurrentTenant(null);
      }
    } catch (error) {
      // Un errore di rete non dice che la sessione e' scaduta: dice che non
      // siamo riusciti a chiederlo. Buttare fuori l'utente per un intoppo
      // significa fargli perdere quello che stava facendo, mentre sul server
      // la sessione e' ancora valida. Se davvero non e' autenticato, la prima
      // chiamata protetta rispondera' 401 e l'interceptor lo portera' al login.
      console.error('Frontend: Network error checking auth status:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuthStatus();

    // Listener for React Native WebView messages
    const handleMessage = (event: any) => {
      try {
        // React Native WebView sends messages as strings, but sometimes objects depending on injection
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        
        if (data && data.type === 'PUSH_TOKEN') {
            console.log('Received push token from mobile shell:', data.token);
            setPushToken({ token: data.token, deviceType: data.deviceType || 'unknown' });
        }
      } catch (e) {
        // Ignore parsing errors, might be other messages
      }
    };

    window.addEventListener('message', handleMessage);
    document.addEventListener('message', handleMessage as any); // For React Native specific

    return () => {
        window.removeEventListener('message', handleMessage);
        document.removeEventListener('message', handleMessage as any);
    };
  }, []);

  // Register token when user is authenticated and token is available
  useEffect(() => {
    if (isAuthenticated && user && pushToken) {
        const registerToken = async () => {
            try {
                await registerNotificationToken(pushToken.token, pushToken.deviceType);
                setNotificationError(null); // Clear any previous errors
            } catch (error: any) {
                setNotificationError(error.message || 'Failed to register notification token.');
            }
        };
        registerToken();
    }
  }, [isAuthenticated, user, pushToken]);

  const login = async (userData: User) => {
    setIsAuthenticated(true);
    setUser(userData);
    if (userData.tenants && userData.tenants.length > 0) {
      const defaultTenant = userData.currentTenantId
        ? userData.tenants.find(t => t.id === userData.currentTenantId)
        : userData.tenants[0];
      setCurrentTenant(defaultTenant || userData.tenants[0]); // Set initial tenant on login
    }
    if (userData.language) {
      i18n.changeLanguage(userData.language);
    }
  };

  const logout = async () => {
    try {
      if (pushToken) {
        await unregisterNotificationToken(pushToken.token);
      }

      const data = await userLogout();
      setIsAuthenticated(false);
      setUser(null);
      setCurrentTenant(null);
      setPushToken(null); // Clear push token on logout
      setNotificationError(null); // Clear notification error on logout
      i18n.changeLanguage('en'); // Reset to default language on logout
      
      if (data.logoutUrl) {
        window.location.href = data.logoutUrl;
      }
    } catch (error: any) {
      setNotificationError(error.message || 'Error during logout.');
      console.error('Network error during logout:', error);
    }
  };

  const switchTenant = async (tenant: Tenant) => {
    if (!user) return;
    try {
      const updatedUser = await updateUser(user.id, { currentTenantId: tenant.id });
      setUser(prevUser => {
        if (!prevUser) return null;
        const newUserData = { ...prevUser, currentTenantId: updatedUser.currentTenantId };
        return newUserData;
      });
      setCurrentTenant(tenant);
      window.location.reload();
    } catch (error) {
      console.error('Failed to switch tenant:', error);
    }
  };

  const updateCurrentTenant = (tenantData: Partial<Tenant>) => {
    setCurrentTenant(prev => prev ? { ...prev, ...tenantData } : prev);
  };

  const updateUserPreferences = async (theme: string, language: string) => {
    if (!user) return;
    try {
      const updatedUser = await updateUser(user.id, { theme, language });
      setUser(updatedUser);
      i18n.changeLanguage(language); // Change i18n language after successful update
    } catch (error) {
      console.error('Failed to update user preferences:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, currentTenant, login, logout, switchTenant, updateCurrentTenant, loading, updateUserPreferences, notificationError, setNotificationError }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
